/* ============================================================================
 * live-socrata.js - browser-side live data service (SODA2 / SoQL)
 * ----------------------------------------------------------------------------
 * Queries the Texas Comptroller mixed-beverage dataset (naix-2893) directly
 * from the browser and shapes the exact payloads the UI consumes - the same
 * aggregations as meridian-server.js, minus the server. All heavy lifting
 * ($select sum(...) $group ...) runs on Socrata's side, so the browser only
 * receives pre-aggregated rows (≤ a few hundred per screen).
 *
 * Dataset: https://dev.socrata.com/foundry/data.texas.gov/naix-2893
 *
 * Tokenless requests share Socrata's per-IP throttle pool, which is fine for
 * modest traffic. An app token (NOT a secret credential per Socrata docs, it
 * only identifies the app for throttling) can be supplied at build time via
 * VITE_SOCRATA_TOKEN or at runtime via window.MERIDIAN_SOCRATA_TOKEN - note
 * a build-time token ships in the public bundle.
 * ==========================================================================*/

const BASE =
  import.meta.env?.VITE_SOCRATA_BASE ||
  (typeof window !== "undefined" && window.MERIDIAN_SOCRATA_BASE) ||
  "https://data.texas.gov/resource/naix-2893.json";

const TOKEN =
  import.meta.env?.VITE_SOCRATA_TOKEN ||
  (typeof window !== "undefined" && window.MERIDIAN_SOCRATA_TOKEN) ||
  "";

const SNAPSHOT_LIMIT = 500;
const PULSE_CITY_COUNT = 6;

/* --------------------------------------------- confirmed field names (v-live) */
const F = {
  permit: "tabc_permit_number",
  name: "location_name",
  address: "location_address",
  city: "location_city",
  zip: "location_zip",
  county: "location_county",              // numeric Texas county code
  obligation: "obligation_end_date_yyyymmdd",
  liquor: "liquor_receipts",
  wine: "wine_receipts",
  beer: "beer_receipts",
  cover: "cover_charge_receipts",
  total: "total_receipts",
};

/* -------------------------------------------- county code -> display name */
import { countyName } from "./tx-counties.js";

/* ---------------------------------------- category inference (approximate) */
function inferCategory(permit, name = "") {
  const n = (name || "").toLowerCase();
  if (/brew|brewing|\bale\b/.test(n)) return "Brewery";
  if (/winery|wine bar|vineyard/.test(n)) return "Wine Bar";
  if (/night ?club|cabaret/.test(n)) return "Nightclub";
  if (/lounge|cantina|club\b/.test(n)) return "Lounge";
  if (/grill|restaurant|kitchen|cafe|café|bistro|steak|taqueria|cocina|eatery/.test(n)) return "Restaurant";
  if (/hotel|resort|marriott|hilton|\binn\b/.test(n)) return "Hotel";
  if (/music|hall|theatre|theater|live|saloon|honky/.test(n)) return "Live Music";
  const p = String(permit || "").trim().toUpperCase();
  if (p.startsWith("RM")) return "Restaurant";
  if (p.startsWith("N")) return "Private Club";
  return "Bar";
}

/* ------------------------------------------------------------- SoQL client */
const esc = (s) => String(s).replace(/'/g, "''");
function monthWindow(key) {                      // "2026-03" -> [startISO, endISO)
  const [y, m] = key.split("-").map(Number);
  const start = `${y}-${String(m).padStart(2, "0")}-01T00:00:00`;
  const ny = m === 12 ? y + 1 : y, nm = m === 12 ? 1 : m + 1;
  const end = `${ny}-${String(nm).padStart(2, "0")}-01T00:00:00`;
  return [start, end];
}
function prevMonthKey(key) {
  let [y, m] = key.split("-").map(Number); m--; if (m < 1) { m = 12; y--; }
  return `${y}-${String(m).padStart(2, "0")}`;
}
const SOQL_TIMEOUT_MS = 45000;
const SOQL_RETRIES = 2;          // total attempts = 1 + retries
const RETRY_DELAY_MS = [2000, 5000];

async function soql(params) {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const headers = { Accept: "application/json" };
  if (TOKEN) headers["X-App-Token"] = TOKEN;

  let lastErr;
  for (let attempt = 0; attempt <= SOQL_RETRIES; attempt++) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), SOQL_TIMEOUT_MS);
    try {
      const res = await fetch(`${BASE}?${qs}`, { headers, signal: ctl.signal });
      clearTimeout(timer);
      if (res.ok) return res.json();
      let detail = ""; try { detail = (await res.text()).slice(0, 160); } catch {}
      lastErr = new Error(`Socrata ${res.status}${detail ? " · " + detail : ""}`);
      // retry only on throttle / upstream errors
      if (res.status !== 429 && res.status < 500) throw lastErr;
    } catch (e) {
      clearTimeout(timer);
      lastErr = e.name === "AbortError" ? new Error(`Socrata timeout after ${SOQL_TIMEOUT_MS / 1000}s`) : e;
    }
    if (attempt < SOQL_RETRIES) await new Promise((r) => setTimeout(r, RETRY_DELAY_MS[attempt] || 5000));
  }
  throw lastErr;
}
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.round(n) : 0; };
const pctChange = (cur, prev) => (prev > 0 ? +(((cur - prev) / prev) * 100).toFixed(1) : 0);

/* ------------------------------------------------------------------- cache */
const cache = new Map();   // key -> Promise (in-flight dedupe + session memo)
function cached(key, fn) {
  if (!cache.has(key)) {
    const p = fn().catch((e) => { cache.delete(key); throw e; });
    cache.set(key, p);
  }
  return cache.get(key);
}

/* ============================================================================
 * AGGREGATIONS (mirror meridian-server.js exactly)
 * ==========================================================================*/
function permitRollup(monthKey, { city, county, limit } = {}) {
  const [start, end] = monthWindow(monthKey);
  const where = [`${F.obligation} >= '${start}' AND ${F.obligation} < '${end}'`];
  if (city && city !== "All cities") where.push(`upper(${F.city}) = upper('${esc(city)}')`);
  if (county != null) where.push(`${F.county} = '${esc(county)}'`);
  return soql({
    $select: [
      F.permit,
      `max(${F.name}) as name`, `max(${F.address}) as addr`, `max(${F.city}) as city`,
      `max(${F.county}) as county`, `max(${F.zip}) as zip`,
      `sum(${F.liquor}) as liquor`, `sum(${F.wine}) as wine`, `sum(${F.beer}) as beer`,
      `sum(${F.cover}) as cover`, `sum(${F.total}) as total`,
    ].join(","),
    $where: where.join(" AND "),
    $group: F.permit,
    $having: "sum(total_receipts) > 0",
    $order: "total DESC",
    $limit: limit || SNAPSHOT_LIMIT,
  });
}

async function marketTotals(monthKey) {
  const [start, end] = monthWindow(monthKey);
  const rows = await soql({
    $select: `count(distinct ${F.permit}) as locations, sum(${F.total}) as sales`,
    $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0`,
  });
  const r = rows[0] || {};
  return { locations: num(r.locations), sales: num(r.sales) };
}

function cityTotals(monthKey, limit) {
  const [start, end] = monthWindow(monthKey);
  return soql({
    $select: `${F.city} as city, sum(${F.total}) as total`,
    $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0`,
    $group: F.city,
    $order: "total DESC",
    $limit: limit || PULSE_CITY_COUNT,
  });
}

/* True scope-wide totals (count + sum) - not capped to the top-N businesses. */
function scopeClause({ city, county }) {
  const w = [];
  if (city && city !== "All cities") w.push(`upper(${F.city}) = upper('${esc(city)}')`);
  if (county != null) w.push(`${F.county} = '${esc(county)}'`);
  return w.length ? " AND " + w.join(" AND ") : "";
}
async function scopedTotals(monthKey, scope) {
  const [start, end] = monthWindow(monthKey);
  const rows = await soql({
    $select: `count(distinct ${F.permit}) as locations, sum(${F.total}) as sales`,
    $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0${scopeClause(scope)}`,
  });
  const r = rows[0] || {};
  return { locations: num(r.locations), sales: num(r.sales) };
}
/* Real trailing-12-month total-sales series for a city/county scope. */
async function scopedTrend(monthKey, scope) {
  const [, end] = monthWindow(monthKey);
  const startYm = shiftYm(monthKey, -11);
  const [sy, sm] = startYm.split("-").map(Number);
  const start = `${sy}-${String(sm).padStart(2, "0")}-01T00:00:00`;
  const rows = await soql({
    $select: `date_trunc_ym(${F.obligation}) as ym, sum(${F.total}) as v`,
    $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0${scopeClause(scope)}`,
    $group: "ym", $order: "ym", $limit: 24,
  });
  const byYm = new Map(rows.map((r) => [String(r.ym).slice(0, 7), num(r.v)]));
  const out = [];
  for (let i = 11; i >= 0; i--) { const ym = shiftYm(monthKey, -i); out.push({ m: ym, v: byYm.get(ym) || 0 }); }
  return out;
}

function shapeBusiness(row, prevTotalByPermit) {
  const total = num(row.total);
  const mix = total > 0 ? {
    l: +(num(row.liquor) / total).toFixed(4),
    w: +(num(row.wine) / total).toFixed(4),
    b: +(num(row.beer) / total).toFixed(4),
    c: +(num(row.cover) / total).toFixed(4),
  } : { l: 0, w: 0, b: 0, c: 0 };
  const prev = prevTotalByPermit ? prevTotalByPermit.get(row[F.permit]) : undefined;
  return {
    id: row[F.permit], permit: row[F.permit],
    name: row.name || "(unnamed permit)", addr: row.addr || "", zip: row.zip || "",
    city: row.city || "–", county: countyName(row.county),
    cat: inferCategory(row[F.permit], row.name),
    total,
    chg: prev !== undefined ? pctChange(total, prev) : null,   // null = no prior filing to compare
    mix,
  };
}

function shiftYm(ym, delta) {
  let [y, m] = ym.split("-").map(Number); m += delta;
  while (m < 1) { m += 12; y--; } while (m > 12) { m -= 12; y++; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

/* ============================================================================
 * dataService contract
 * ==========================================================================*/
export const socrataDataService = {
  months() {
    /* min/max is one aggregate row instead of a group-by over 3.78M rows;
     * statewide filings exist every month, so the synthesized range is the
     * same list the group-by produced, at a fraction of the query cost. */
    return cached("months", async () => {
      const rows = await soql({ $select: `min(${F.obligation}) as lo, max(${F.obligation}) as hi` });
      const lo = String(rows[0]?.lo || "").slice(0, 7);
      const hi = String(rows[0]?.hi || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(lo) || !/^\d{4}-\d{2}$/.test(hi)) throw new Error("Socrata: bad month range");
      const months = [];
      let [y, m] = lo.split("-").map(Number);
      const [hy, hm] = hi.split("-").map(Number);
      while (y < hy || (y === hy && m <= hm)) {
        months.push(`${y}-${String(m).padStart(2, "0")}`);
        m++; if (m > 12) { m = 1; y++; }
      }
      return { months, latest: months[months.length - 1] };
    });
  },

  /** Every city reporting in the month (for the City filter). */
  cityList(month) {
    return cached(`cities:${month}`, async () => {
      const rows = await cityTotals(month, 500);
      return [...new Set(rows.map((r) => r.city).filter(Boolean))].sort();
    });
  },

  /** Per-county totals for the month (sales map): [{code, total, venues}]. */
  countyTotals(month) {
    return cached(`countyTotals:${month}`, async () => {
      const [start, end] = monthWindow(month);
      const rows = await soql({
        $select: `${F.county} as code, sum(${F.total}) as total, count(distinct ${F.permit}) as venues`,
        $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0`,
        $group: F.county, $order: "total DESC", $limit: 300,
      });
      return rows
        .filter((r) => r.code != null && String(r.code).trim() !== "")
        .map((r) => ({ code: parseInt(String(r.code).trim(), 10), total: num(r.total), venues: num(r.venues) }));
    });
  },

  /** Top cities by reported sales (Cities directory): [{city, county, total, venues}]. */
  cityStats(month, limit = 60) {
    return cached(`cityStats:${month}:${limit}`, async () => {
      const [start, end] = monthWindow(month);
      const rows = await soql({
        $select: `${F.city} as city, max(${F.county}) as county, sum(${F.total}) as total, count(distinct ${F.permit}) as venues`,
        $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ${F.total} > 0`,
        $group: F.city, $order: "total DESC", $limit: limit,
      });
      return rows.filter((r) => r.city).map((r) => ({
        city: r.city, county: countyName(r.county), total: num(r.total), venues: num(r.venues),
      }));
    });
  },

  snapshot(month, { city = "All cities", county = null, limit = SNAPSHOT_LIMIT } = {}) {
    if (!/^\d{4}-\d{2}$/.test(month || "")) return Promise.reject(new Error("month=YYYY-MM required"));
    const scoped = (city && city !== "All cities") || county != null;
    return cached(`snap:${month}:${city}:${county ?? ""}:${limit}`, async () => {
      const prev = prevMonthKey(month);
      /* Scoped (city/county) pages compute their own metrics from the rows,
       * so skip the statewide market-total + city-pulse queries entirely. */
      if (scoped) {
        const scope = { city, county };
        const [cur, curPrev, totals, totalsPrev, trend] = await Promise.all([
          permitRollup(month, { city, county, limit }),
          permitRollup(prev, { city, county, limit: 2000 }),
          scopedTotals(month, scope), scopedTotals(prev, scope),
          scopedTrend(month, scope),
        ]);
        const prevByPermit = new Map(curPrev.map((r) => [r[F.permit], num(r.total)]));
        const businesses = cur.map((r) => shapeBusiness(r, prevByPermit));
        const top = businesses[0];
        const metrics = {
          trackedLocations: totals.locations,
          trackedDelta: pctChange(totals.locations, totalsPrev.locations),
          reportedSales: totals.sales,
          salesDelta: pctChange(totals.sales, totalsPrev.sales),
          avgLocation: totals.locations ? Math.round(totals.sales / totals.locations) : 0,
          avgDelta: pctChange(
            totals.locations ? totals.sales / totals.locations : 0,
            totalsPrev.locations ? totalsPrev.sales / totalsPrev.locations : 0),
          highestGrossing: top ? { name: top.name, city: top.city, county: top.county, total: top.total, chg: top.chg, permit: top.permit } : null,
        };
        return { period: month, prior: prev, metrics, pulse: [], businesses, trend };
      }
      const [cur, curPrev, totals, totalsPrev, cities, citiesPrev] = await Promise.all([
        permitRollup(month, { city, county, limit }),
        permitRollup(prev, { city, county, limit: 2000 }),
        marketTotals(month), marketTotals(prev),
        cityTotals(month, PULSE_CITY_COUNT), cityTotals(prev, 50),
      ]);
      const prevByPermit = new Map(curPrev.map((r) => [r[F.permit], num(r.total)]));
      const businesses = cur.map((r) => shapeBusiness(r, prevByPermit));

      const prevCity = new Map(citiesPrev.map((r) => [r.city, num(r.total)]));
      const pulse = cities.map((r) => ({
        city: r.city, total: num(r.total), chg: pctChange(num(r.total), prevCity.get(r.city) || 0),
      }));

      const top = businesses[0];
      const metrics = {
        trackedLocations: totals.locations,
        trackedDelta: pctChange(totals.locations, totalsPrev.locations),
        reportedSales: totals.sales,
        salesDelta: pctChange(totals.sales, totalsPrev.sales),
        avgLocation: totals.locations ? Math.round(totals.sales / totals.locations) : 0,
        avgDelta: pctChange(
          totals.locations ? totals.sales / totals.locations : 0,
          totalsPrev.locations ? totalsPrev.sales / totalsPrev.locations : 0),
        highestGrossing: top ? { name: top.name, city: top.city, county: top.county, total: top.total, chg: top.chg, permit: top.permit } : null,
      };
      return { period: month, prior: prev, metrics, businesses, pulse };
    });
  },

  business(permit) {
    return cached(`biz:${permit}`, async () => {
      const rows = await soql({
        $select: [
          `date_trunc_ym(${F.obligation}) as ym`,
          `max(${F.name}) as name`, `max(${F.address}) as addr`, `max(${F.city}) as city`,
          `max(${F.county}) as county`, `max(${F.zip}) as zip`,
          `sum(${F.liquor}) as liquor`, `sum(${F.wine}) as wine`, `sum(${F.beer}) as beer`,
          `sum(${F.cover}) as cover`, `sum(${F.total}) as total`,
        ].join(","),
        $where: `${F.permit} = '${esc(permit)}'`,
        $group: "ym", $order: "ym", $limit: 400,
      });
      if (!rows.length) throw new Error("Texas Bar Sales API 404 · permit_not_found");
      const series = rows.map((r) => ({
        ym: String(r.ym).slice(0, 7),
        liquor: num(r.liquor), wine: num(r.wine), beer: num(r.beer), cover: num(r.cover), total: num(r.total),
      }));
      const last = rows[rows.length - 1];
      const latest = series[series.length - 1];
      const trend = series.slice(-12).map((s) => ({ m: s.ym, v: s.total }));
      const mix = latest.total > 0 ? {
        l: +(latest.liquor / latest.total).toFixed(4), w: +(latest.wine / latest.total).toFixed(4),
        b: +(latest.beer / latest.total).toFixed(4), c: +(latest.cover / latest.total).toFixed(4),
      } : { l: 0, w: 0, b: 0, c: 0 };
      const prevYear = series.length > 12 ? series[series.length - 13].total : null;
      const yoy = prevYear ? pctChange(latest.total, prevYear) : 0;
      /* Full history, newest first - the UI shows 12 months on Free and the
       * complete series on Pro. */
      const historical = series.slice().reverse().map((s) => {
        const yrAgo = series.find((x) => x.ym === shiftYm(s.ym, -12));
        return {
          month: s.ym, liquor: s.liquor, wine: s.wine, beer: s.beer, cover: s.cover, total: s.total,
          yoy: yrAgo ? pctChange(s.total, yrAgo.total) : 0,
        };
      });
      const monthMoM = series.length > 1 ? pctChange(latest.total, series[series.length - 2].total) : 0;
      return {
        business: {
          id: permit, permit,
          name: last.name, addr: last.addr, zip: last.zip,
          city: last.city, county: countyName(last.county),
          cat: inferCategory(permit, last.name),
          total: latest.total, chg: monthMoM, period: latest.ym,
        },
        trend, historical, yoy, mix,
      };
    });
  },

  async search(q, month, limit = 25) {
    const query = (q || "").trim();
    if (!query) return { results: [] };
    if (!/^\d{4}-\d{2}$/.test(month || "")) throw new Error("month=YYYY-MM required");
    const [start, end] = monthWindow(month);
    const s = esc(query);
    const rows = await soql({
      $select: [F.permit, `max(${F.name}) as name`, `max(${F.city}) as city`, `max(${F.county}) as county`,
        `sum(${F.total}) as total`].join(","),
      $where: `${F.obligation} >= '${start}' AND ${F.obligation} < '${end}' AND ` +
        `(upper(${F.name}) like upper('%${s}%') OR upper(${F.city}) like upper('%${s}%') ` +
        `OR ${F.zip} like '%${s}%' OR upper(${F.permit}) like upper('%${s}%'))`,
      $group: F.permit, $order: "total DESC", $limit: Math.min(limit, 100),
    });
    return {
      results: rows.map((r) => ({
        id: r[F.permit], permit: r[F.permit], name: r.name, city: r.city,
        county: countyName(r.county), total: num(r.total), cat: inferCategory(r[F.permit], r.name),
      })),
    };
  },
};

export default socrataDataService;
